/**
 * What a finished tick *reports* — the aero readout the HUD and the AI read, and
 * the edge-triggered event list.
 *
 * Split out of `step.ts` when AD-007n's elevator pushed that file past the
 * 400-line gate; the integrator itself is the other half. Nothing here decides
 * anything, which is why it separates cleanly: every value is read off a state
 * that has already been advanced.
 */

import type { Grounding } from "./ground-contact.ts";
import type { GunResult } from "./guns.ts";
import type { MotionResult } from "./step.ts";
import type { AeroReadout, SimEventKind, SimState } from "./state.ts";
import { bankAngle } from "./math3d.ts";
import { metresToFeet } from "./units.ts";

export interface TickSignals {
  readonly wingFailedNow: boolean;
  readonly guns: GunResult;
  readonly grounding: Grounding;
}

export function collectEvents(previous: SimState, next: SimState, signals: TickSignals): SimEventKind[] {
  const events: SimEventKind[] = [];
  if (next.readout.stalled && !previous.readout.stalled) events.push("stallEntered");
  if (!next.readout.stalled && previous.readout.stalled) events.push("stallRecovered");
  if (next.stallModes.spin.active && !previous.stallModes.spin.active) events.push("spinEntered");
  if (!next.stallModes.spin.active && previous.stallModes.spin.active) events.push("spinRecovered");
  if (next.stallModes.spiral.active && !previous.stallModes.spiral.active) events.push("spiralEntered");
  if (signals.wingFailedNow) events.push("wingFailure");
  if (signals.guns.jammedNow) events.push("gunJammed");
  if (signals.guns.permanentNow) events.push("gunJamPermanent");
  if (previous.guns.jammed && !next.guns.jammed) events.push("gunCleared");
  if (next.engine.carbIced && !previous.engine.carbIced) events.push("carburettorIced");
  if (!next.engine.carbIced && previous.engine.carbIced) events.push("carburettorThawed");
  if (next.engine.fuelExhausted && !previous.engine.fuelExhausted) events.push("fuelExhausted");
  if (next.pilot.condition === "dizzy" && previous.pilot.condition === "normal") events.push("hypoxiaDizzy");
  if (next.pilot.condition === "blackout" && previous.pilot.condition !== "blackout") {
    events.push("hypoxiaBlackout");
  }
  if (next.pilot.condition === "normal" && previous.pilot.condition !== "normal") {
    events.push("hypoxiaRecovered");
  }
  const warningSeconds = next.config.tunables.woundBleedOutWarningSeconds;
  if (
    next.pilot.wounded &&
    next.pilot.bleedOutSeconds >= 0 &&
    previous.pilot.bleedOutSeconds > warningSeconds &&
    next.pilot.bleedOutSeconds <= warningSeconds
  ) {
    events.push("bleedOutWarning");
  }
  if (!next.pilot.alive && previous.pilot.alive) events.push("pilotDied");
  // Edge-triggered: fires on the tick the aircraft touches down, not on every
  // tick it then spends resting on the ground (see the note above
  // `SimEventKind`). `readout.grounded` is the continuous state to poll.
  if (signals.grounding.touchedDown) events.push("groundContact");
  if (signals.grounding.liftedOff) events.push("liftedOff");
  if (signals.grounding.collapsedNow) events.push("gearCollapsed");
  if (signals.grounding.landedNow) events.push("landed");
  if (signals.grounding.crashed && !previous.crashed) events.push("crashed");
  return events;
}

export function readoutOf(
  motion: MotionResult,
  grounding: Grounding,
  /** How high the pose origin stands above the surface at this tick's attitude (`aircraft-model-frame.ts`'s `stanceHeightM`) — what makes AGL zero on the wheels. */
  stanceM: number,
  stalled: boolean,
): AeroReadout {
  return {
    airspeedMps: motion.forces.airData.airspeedMps,
    alphaRad: motion.forces.airData.alphaRad,
    betaRad: motion.forces.airData.betaRad,
    loadFactor: motion.forces.loadFactor,
    stalled,
    altitudeM: grounding.position.y,
    altitudeFt: metresToFeet(grounding.position.y),
    groundHeightM: grounding.sample.heightM,
    altitudeAglM: Math.max(0, grounding.position.y - grounding.sample.heightM - stanceM),
    climbRateMps: grounding.velocity.y,
    bankRad: bankAngle(grounding.orientation),
    turnLimited: motion.forces.turnLimited,
    grounded: grounding.contacted,
    windMps: motion.wind,
  };
}


